import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { HpmsTextHtmlAPI } from '@philips-emr/hpms-components/lib/components/hpms-text-html/hpms-text-html-api';
import { ModalAPI, PanelAPI, Stamp, StampIcon } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnClick, OnClose, OnDynamicContentRender, OnMount } from '@philips-emr/tws-core/lib/client';
import { FetchEvent, RenderEvent } from '@philips-emr/tws-core/lib/component/types';
import { MessageService } from '@philips-emr/tws-core/lib/services';

import './privacy.scss';
import { FragmentNames } from '../../codegen';
import { PrivacyAliases as Aliases } from '../../codegen/constants/aliases/privacy-aliases';
import BaseController from '../base/base-controller';

interface TermOfUse {
  status: Status;
  dsTerm: string;
}

interface PrivacyWarning {
  status: Status;
  dsTerm: string;
}

enum Status {
  N = 'N',
  S = 'S',
}

@Controller({ fragment: FragmentNames.PRIVACY })
export default class PrivacyController extends BaseController {

  @Inject(Aliases.PANEL_TERM)
  private panelTerm: PanelAPI;

  @Inject(Aliases.PANEL_PRIVACY_WARNING)
  private panelPrivacyWarning: PanelAPI;

  @Inject(Aliases.PANEL_MODAL_DESCRIPTION)
  private panelModal: PanelAPI;

  @Inject(Aliases.PANEL_PRIVACY_WARNING_TEXT)
  private panelPrivacyWarningText: PanelAPI;

  @Inject(Aliases.PANEL_TERM_TEXT)
  private panelTermText: PanelAPI;

  @Inject(Aliases.HPMS_TEXT_HTML_PRIVACY_WARNING)
  private hpmsTextPrivacyWarning: HpmsTextHtmlAPI;

  @Inject(Aliases.HPMS_TEXT_HTML_TERM)
  private hpmsTextTermOfUse: HpmsTextHtmlAPI;

  @Inject(Aliases.MODAL_DESCRIPTION)
  private modalDescription: ModalAPI;

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_FIELD)
  onDynamicContentRenderPrivacyWarning(event: RenderEvent): void {
    const { data, render } = event;
    const { status } = data as PrivacyWarning;
    if (status) {
      render((): ReactElement => <Stamp icon={this.getIconByStatus(status)} color={this.getColorByStatus(status)} label={this.getLabelByStatus(status)} />);
    }
  }

  @OnDynamicContentRender(Aliases.HPMS_DYNAMIC_CONTENT_FIELD_TERM)
  onDynamicContentRenderTermOfUse(event: RenderEvent): void {
    const { data, render } = event;
    const { status } = data as TermOfUse;
    if (status) {
      render((): ReactElement => <Stamp icon={this.getIconByStatus(status)} color={this.getColorByStatus(status)} label={this.getLabelByStatus(status)} />);
    }
  }

  @OnAfterFetch(Aliases.FETCH_PRIVACY_WARNING)
  onAfterFetchPrivacyWarning(event: FetchEvent): void {
    if (event.error) {
      this.panelPrivacyWarning.setVisible(false);
    }
    else if (!event.data) {
      this.panelPrivacyWarning.setVisible(false);
    }
    else {
      const data = event.data as PrivacyWarning;
      const { dsTerm } = data;
      const descriptionPrivacyWarning = dsTerm;
      this.context.set({ descriptionPrivacyWarning });
    }
  }

  @OnAfterFetch(Aliases.FETCH_TERM)
  onAfterFetchTerm(event: FetchEvent): void {
    if (event.error) {
      this.panelTerm.setVisible(false);
    }
    else if (!event.data) {
      this.panelTerm.setVisible(false);
    }
    else {
      const data = event.data as TermOfUse;
      const { dsTerm } = data;
      const descriptionTermOfUse = dsTerm;
      this.context.set({ descriptionTermOfUse });
    }
  }

  @OnClick(Aliases.CARD_PRIVACY_WARNING)
  onClickCardPrivacyWarning(): void {
    this.panelModal.setVisible(true);
    this.context.set({ isPrivacyWarning: true });
  }

  @OnClick(Aliases.CARD_TERM)
  onClickCardTerm(): void {
    this.panelModal.setVisible(true);
    this.context.set({ isTerm: true });
  }

  @OnMount(Aliases.MODAL_DESCRIPTION)
  onMountModal(): void {
    const isPrivacyWarning = this.context.get('isPrivacyWarning') as boolean;
    const isTerm = this.context.get('isTerm') as boolean;
    if (isPrivacyWarning) {
      this.modalDescription.setTitle(MessageService.translate('beneficiary.aviso-privacidade'));
      this.panelPrivacyWarningText.setVisible(true);
    }
    if (isTerm) {
      this.modalDescription.setTitle(MessageService.translate('beneficiary.termos-uso'));
      this.panelTermText.setVisible(true);
    }
  }

  @OnMount(Aliases.FIELD_PRIVACY_WARNING_TEXT)
  onMountAvisoTexto(): void {
    const descriptionPrivacyWarning = this.context.get('descriptionPrivacyWarning') as string;
    this.hpmsTextPrivacyWarning.setData(descriptionPrivacyWarning);
  }

  @OnMount(Aliases.FIELD_TERM_TEXT)
  onMountTermoTexto(): void {
    const descriptionTermOfUse = this.context.get('descriptionTermOfUse') as string;
    this.hpmsTextTermOfUse.setData(descriptionTermOfUse);
  }

  @OnClose(Aliases.MODAL_DESCRIPTION)
  onCloseModal(): void {
    this.panelModal.setVisible(false);
    this.context.set({ isPrivacyWarning: false, isTerm: false  });
  }

  private getIconByStatus(status: Status): StampIcon {
    const icon = {
      [Status.N]: StampIcon.CHECK,
      [Status.S]: StampIcon.CANCELED,
    };

    return icon[status];
  }

  private getColorByStatus(status: Status): Color {
    const color = {
      [Status.N]: Color.SAFETY_GREEN,
      [Status.S]: Color.SAFETY_RED,
    };

    return color[status];
  }

  private getLabelByStatus(status: Status): string {
    const message = (status == Status.N) ? 'beneficiary.in-force' : 'beneficiary.overdue';
    return MessageService.translate(message);
  }
}
